import { ALL_ACTIONS } from './Action';

import type { Action } from './Action';
import type { CommunityId } from 'domain/CommunityId';

/**
 * The port.
 *
 * Shaped as `permissions-service`'s `GET /v1/permissions/me` response, plus the scope it was
 * fetched for, so that the adapter producing it can be swapped without touching anything above.
 */

/**
 * Exactly one target per snapshot, matching the endpoint's contract (ADR-0007).
 *
 * `community:setEntitlements` is the awkward one. It TARGETS a community, but the rule is
 * organisation-level - organisation admin - so it needs a community-scoped
 * snapshot, which is why the organisation console wraps its community wizard in one for that single
 * check (U6). Open on HNS-349.
 */
export type PermissionsScope =
  | { communityId: CommunityId; organisationId?: never }
  | { organisationId: number; communityId?: never };

/** Which target a snapshot speaks about. Two kinds, matching the endpoint's contract (ADR-0007). */
export type PermissionsScopeKind = 'community' | 'organisation';

export const scopeKindOf = (scope: PermissionsScope): PermissionsScopeKind =>
  scope.organisationId !== undefined ? 'organisation' : 'community';

/**
 * One scope as one comparable string.
 *
 * In `core` rather than beside its first caller, because unrelated things key state on a scope -
 * the retry backoff and the outage report.
 */
export const scopeTag = (scope: PermissionsScope | undefined): string | undefined => {
  if (scope === undefined) return undefined;

  return scope.organisationId !== undefined ? `organisation:${scope.organisationId}` : `community:${scope.communityId}`;
};

/**
 * One place answers come from, and the set of questions it speaks for.
 *
 * A snapshot can be blended from several, though today it holds one: permissions-service. Each
 * source declares what it could contribute rather than claiming ownership, so determinacy is a union
 * across contributors.
 */
export type PermissionsSource = {
  /**
   * What this source grants. Read only once the source has settled: a source that is still loading
   * has not answered, and one that could not answer has nothing to say, so neither grants.
   */
  allowed: ReadonlySet<Action>;
  /**
   * Every action this source could grant, whether or not it currently does. Declaring it is what
   * makes a denial determinable: an action no source contributes is a determined no rather than a
   * gate waiting on an answer that can only ever be no.
   */
  contributes: ReadonlySet<Action>;
  /** No answer yet. Gates hold rather than deny and redirect - unless another source grants. */
  isLoading: boolean;
  /**
   * The answer could not be determined. Denies, per the service's "treat unreachable as denied",
   * while staying distinguishable from a determined denial - and, again, unless another source
   * grants, so an outage here cannot take away a capability derived somewhere else.
   */
  isError: boolean;
};

/**
 * How a consumer asks the sources again, and whether it may.
 *
 * On the port rather than on the gate, because a retry control cannot be gate-local: `Can` and
 * `CanRoute` import nothing but `useCan`, and reaching `permissionsApi` from a gate would break the
 * one-file adapter choice the whole layer is built around (R19).
 */
export type PermissionsRetry = {
  /**
   * Ask again. A no-op under an adapter with nothing to re-ask - the legacy one derives from queries
   * it does not own - so a gate can offer the control without knowing which adapter answered.
   */
  retry: () => void;
  /**
   * Whether calling `retry` would do anything.
   *
   * False while a request is in flight, and false inside the backoff window after a failure. A
   * degraded service otherwise gets a retry button pressed by every affected user at once, which is
   * the moment it can least afford it. The gate reads this to disable the control; the interval
   * itself is the adapter's business.
   */
  canRetry: boolean;
};

/** An adapter that cannot re-ask. Shared so the snapshot keeps one identity across renders. */
export const NO_RETRY: PermissionsRetry = { retry: () => undefined, canRetry: false };

export type PermissionsSnapshot = PermissionsRetry & {
  /**
   * Every source contributing to this answer. Order is not meaningful; `evaluate` unions them.
   * An empty list grants nothing, and grants nothing undetermined.
   */
  sources: readonly PermissionsSource[];
  /**
   * What the sources are about. `undefined` when there is nothing to scope to - a signed-in user
   * with no community - in which case nothing community-scoped can be granted. Required rather than
   * optional so that an adapter has to answer the question. Snapshot-wide rather than per source,
   * because a snapshot has exactly one target (ADR-0007) whoever answered for it.
   */
  scope: PermissionsScope | undefined;
};

/**
 * For an adapter that is the whole answer on its own, as the legacy one is: it derives every action
 * in the vocabulary, so nothing else can be waiting to contribute one.
 */
export const CONTRIBUTES_EVERY_ACTION: ReadonlySet<Action> = new Set(ALL_ACTIONS);
